import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  getDefaultNetwork,
  NETWORK_ADMIN_OVERRIDE_KEY,
  NETWORK_STORAGE_KEY,
  type Network,
} from '../lib/networkConfig.js';

interface NetworkContextType {
  network: Network;
  toggleNetwork: () => void;
  isDevnet: boolean;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export function NetworkProvider({ children }: { children: React.ReactNode }) {
  const [network, setNetwork] = useState<Network>('mainnet-beta');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setNetwork(getDefaultNetwork());
    }
  }, []);

  const toggleNetwork = useCallback(() => {
    if (typeof window === 'undefined') return;

    setNetwork((prev) => {
      const next: Network = prev === 'mainnet-beta' ? 'devnet' : 'mainnet-beta';
      localStorage.setItem(NETWORK_STORAGE_KEY, next);
      localStorage.setItem(NETWORK_ADMIN_OVERRIDE_KEY, 'true');
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        toggleNetwork();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleNetwork]);

  return (
    <NetworkContext.Provider
      value={{ network, toggleNetwork, isDevnet: network === 'devnet' }}
    >
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetwork(): NetworkContextType {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within NetworkProvider');
  }
  return context;
}
